import { useEffect, useState } from "react";

import dayjs from "dayjs";
import "dayjs/locale/ko";
import { AiOutlineClose } from "react-icons/ai";
import { useShallow } from "zustand/react/shallow";

import { fetchReservation, reservation } from "@/apis/reservation";
import AlertModal from "@/components/Modal/AlertModal";
import ModalLayout from "@/components/Layout/ModalLayout";
import useModal from "@/hooks/useModal";
import useCalendarStore from "@/store/calendarStore";
import useLoginStore from "@/store/loginStore";
import useUserStore from "@/store/userStore";
import type { ReservationResponse } from "@/types/reservation";
import { getTime } from "@/utils/reservation";
import TimeTablePicker from "@/pages/Reservation/components/ReservationTab/PlaceSearchTab/TimeTablePicker";

dayjs.locale("ko");

const formatTime = (t: number) => {
  const h = Math.floor(t);
  const m = t % 1 === 0.5 ? "30" : "00";
  return `${String(h).padStart(2, "0")}:${m}`;
};

interface QuickReservationModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const QuickReservationModal = ({ onClose, onSuccess }: QuickReservationModalProps) => {
  const [date, selectedPlace] = useCalendarStore(
    useShallow(state => [state.date, state.selectedPlace]),
  );
  const { tokenId } = useLoginStore();
  const user = useUserStore();

  const [selectedTimes, setSelectedTimes] = useState<number[]>([]);
  const [reservationList, setReservationList] = useState<ReservationResponse[]>([]);
  const [purpose, setPurpose] = useState("");
  const [headcount, setHeadcount] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const alertModal = useModal();
  const [alertMsg, setAlertMsg] = useState("");

  const placeName = selectedPlace?.plcNm || "";
  const plcCd = selectedPlace?.plcCd || "";
  const isPastDate = date.isBefore(dayjs(), "day");

  // Fetch existing reservations for the selected date+place
  useEffect(() => {
    if (!plcCd) return;
    const load = async () => {
      const data = await fetchReservation(date.format("YYYYMMDD"), plcCd);
      setReservationList(data || []);
    };
    load();
  }, [date, plcCd]);

  const handleSubmit = async () => {
    if (selectedTimes.length === 0) {
      setAlertMsg("시간을 선택해주세요.");
      alertModal.onOpen();
      return;
    }
    if (!purpose.trim()) {
      setAlertMsg("사용 목적을 입력해주세요.");
      alertModal.onOpen();
      return;
    }
    if (!headcount.trim()) {
      setAlertMsg("사용 인원을 입력해주세요.");
      alertModal.onOpen();
      return;
    }

    const startTime = getTime({ times: selectedTimes, type: "startTime" });
    const endTime = getTime({ times: selectedTimes, type: "endTime" });

    setSubmitting(true);
    try {
      await reservation({
        useCnts: purpose,
        cpsCd: "PTK",
        bldCd: "PTK_PTK",
        plcCd,
        rsvtDt: date.format("YYYYMMDD"),
        startTime,
        endTime,
        mbrId: tokenId,
      });
      onSuccess();
    } catch {
      setAlertMsg("예약에 실패했습니다. 다시 시도해주세요.");
      alertModal.onOpen();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalLayout variant="primary" onClose={onClose}>
      <div className="flex w-[340px] flex-col sm:w-[680px]">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h3 className="text-base font-bold text-gray-900">예약 신청</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <AiOutlineClose size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-4 px-6 py-5">
          {/* Date & Place (read-only) */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">날짜</label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {date.format("YYYY년 M월 D일 (dd)")}
              </div>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">장소</label>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {placeName || "장소를 선택해주세요"}
              </div>
            </div>
          </div>

          {/* Time Table */}
          <div>
            <div className="mb-1 flex items-center justify-between pb-1">
              <label className="text-xs font-medium text-gray-500">시간 선택</label>
              {selectedTimes.length > 0 && (
                <span className="rounded-full bg-primary/10 px-3 py-0.5 text-xs font-semibold text-primary">
                  {formatTime(Math.min(...selectedTimes))} ~ {formatTime(Math.max(...selectedTimes) + 0.5)}
                </span>
              )}
            </div>
            <div className="overflow-x-auto">
              <TimeTablePicker
                selectedDate={date.toDate()}
                reservationList={reservationList}
                selectedTimes={selectedTimes}
                onChange={setSelectedTimes}
                showBadge={false}
              />
            </div>
          </div>

          {/* User info (read-only) */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">예약자</label>
              <div className="min-h-[38px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {user.userName}
              </div>
            </div>
            <div className="flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-500">연락처</label>
              <div className="min-h-[38px] rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {user.telNo || "\u00A0"}
              </div>
            </div>
          </div>

          {/* Purpose */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">사용 목적</label>
            <input
              type="text"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="사용 목적을 입력하세요 (최대 15자)"
              maxLength={15}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
            />
          </div>

          {/* Headcount */}
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-500">사용 인원</label>
            <input
              type="number"
              value={headcount}
              onChange={(e) => setHeadcount(e.target.value)}
              placeholder="예상 사용 인원 수"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 transition-colors focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || isPastDate}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "신청 중..." : "예약 신청"}
          </button>
        </div>
      </div>

      {alertModal.isOpen && (
        <AlertModal onClose={alertModal.onClose}>
          {alertMsg}
        </AlertModal>
      )}
    </ModalLayout>
  );
};

export default QuickReservationModal;
